"use client";

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { MediaPlayerInstance } from "@vidstack/react";
import { PlaylistItem } from "@/APITypes/PlaylistItem.cs";
import { SessionPlaylistItem } from "@/types";
import TrackEvent from "@/lib/trackEvent";
import { segment } from "@/APITypes/Segment.cs";
import { toNearestHighlight, toRecordingClock } from "@/lib/recording-clock";

export function usePlaylistState({
	playerInstance,
	isPaused,
	setIsPaused,
	isBusinessCardPlaying,
	currentPlaybackTime,
	isSeeking,
	setIsSeeking,
	setSkipToTime,
}: {
	playerInstance: MediaPlayerInstance | null;
	isPaused: boolean;
	setIsPaused: (v: boolean) => void;
	isBusinessCardPlaying: boolean;
	currentPlaybackTime: number;
	isSeeking: boolean;
	setIsSeeking: (v: boolean) => void;
	setSkipToTime: (ms: number) => void;
}) {
	const [currentPlaylist, setCurrentPlaylist] = useState<SessionPlaylistItem[]>(
		[],
	);
	const [currentSessionIndex, setCurrentSessionIndex] = useState(0);
	const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
	const [currentSegment, setCurrentSegment] = useState<segment | null>(null);
	const [currentHighlightIndex, setCurrentHighlightIndex] = useState<
		number | null
	>(null);
	const [isHighlightsOnlyMode, setIsHighlightsOnlyMode] = useState(false);
	const [isPortal, setIsPortal] = useState(false);
	const [segmentsWatched, setSegmentsWatched] = useState<string[]>([]);

	const lastReportedTimestampRef = useRef<Date | null>(null);

	// A mode switch keeps the viewer's place in the session. The other stream
	// has to load before it can seek, so the seek waits here until it can play.
	const pendingModeSeekRef = useRef<{
		index: number;
		hlOnly: boolean;
		ms: number;
	} | null>(null);

	// Always-fresh snapshot — updated synchronously after every render (before
	// useEffect callbacks fire) so tracking functions never read stale closures.
	const stateRef = useRef({
		currentSegment,
		currentPlaylist,
		currentSessionIndex,
		isHighlightsOnlyMode,
		segmentsWatched,
		isPaused,
		isSeeking,
		currentPlaybackTime,
		isPortal,
		isBusinessCardPlaying,
	});

	useLayoutEffect(() => {
		stateRef.current = {
			currentSegment,
			currentPlaylist,
			currentSessionIndex,
			isHighlightsOnlyMode,
			segmentsWatched,
			isPaused,
			isSeeking,
			currentPlaybackTime,
			isPortal,
			isBusinessCardPlaying,
		};
	});

	// ─── derived ──────────────────────────────────────────────────────────────

	const firstIndexWithHighlights = useMemo(
		() =>
			Math.max(
				currentPlaylist.findIndex((item) => item.highlights.length > 0),
				0,
			),
		[currentPlaylist],
	);

	const session = useMemo<SessionPlaylistItem | null>(() => {
		if (!currentPlaylist.length) return null;
		let idx = currentSessionIndex;
		let s = currentPlaylist[idx];
		if (isHighlightsOnlyMode) {
			while (!s?.highlightsOnlyStream && idx < currentPlaylist.length - 1) {
				idx++;
				s = currentPlaylist[idx];
			}
			if (!s?.highlightsOnlyStream) return null;
		}
		return s ?? null;
	}, [currentSessionIndex, isHighlightsOnlyMode, currentPlaylist]);

	// ─── session navigation ───────────────────────────────────────────────────

	// Find the first valid session index from `from` onwards in highlights-only mode.
	// Returns -1 if no valid session exists.
	function resolveHlOnlyIndex(from: number, pl: SessionPlaylistItem[]): number {
		let idx = from;
		while (!pl[idx]?.highlightsOnlyStream && idx < pl.length - 1) idx++;
		return pl[idx]?.highlightsOnlyStream ? idx : -1;
	}

	// Public setter wired to context — resets watched segments and (in
	// highlights-only mode) advances past sessions that have no stream.
	const goToSession = useCallback(
		(next: number | ((prev: number) => number)) => {
			const { isPortal: portal, isHighlightsOnlyMode: hlOnly, currentPlaylist: pl, currentSessionIndex: cur } =
				stateRef.current;

			const requested = typeof next === "function" ? next(cur) : next;

			if (!portal) setSegmentsWatched([]);

			if (hlOnly) {
				const resolved = resolveHlOnlyIndex(requested, pl);
				if (resolved === -1) {
					setCurrentSessionIndex(0);
					playerInstance?.pause();
				} else {
					setCurrentSessionIndex(resolved);
				}
			} else {
				setCurrentSessionIndex(requested);
			}
		},
		[playerInstance],
	);

	// Toggles highlights-only mode and immediately resolves the session index.
	// When the session stays, the other stream picks up where this one was:
	// the same moment, or the closest highlight when that moment isn't one.
	// A session with no highlights stream starts over on the next one that has.
	const changeHighlightsOnlyMode = useCallback(
		(value: boolean) => {
			const {
				isPortal: portal,
				currentSessionIndex: idx,
				currentPlaylist: pl,
				isHighlightsOnlyMode: wasHlOnly,
				currentPlaybackTime: time,
				isBusinessCardPlaying: bio,
			} = stateRef.current;
			if (value === wasHlOnly) return;
			if (!portal) setSegmentsWatched([]);
			setIsHighlightsOnlyMode(value);
			pendingModeSeekRef.current = null;
			if (value) {
				const resolved = resolveHlOnlyIndex(idx, pl);
				if (resolved === -1) {
					setCurrentSessionIndex(0);
					playerInstance?.pause();
					return;
				}
				if (resolved !== idx) {
					setCurrentSessionIndex(resolved);
					return;
				}
			}
			// Same check the stream picker makes: without a highlights stream
			// the source doesn't change, so there's no load to wait for.
			const s = pl[idx];
			if (bio || time <= 0 || !s?.highlightsOnlyStream?.src) return;
			const ms = value
				? toNearestHighlight(time * 1000, s.segments)
				: toRecordingClock(time * 1000, s.segments, true);
			if (ms === null) return;
			pendingModeSeekRef.current = { index: idx, hlOnly: value, ms };
			// Keeps segment tracking from logging the new stream's first
			// highlight while it sits at 0 waiting to seek.
			setIsSeeking(true);
		},
		[playerInstance, setIsSeeking],
	);

	useEffect(() => {
		if (!playerInstance) return;
		const onCanPlay = () => {
			const target = pendingModeSeekRef.current;
			pendingModeSeekRef.current = null;
			const { currentSessionIndex: idx, isHighlightsOnlyMode: hlOnly } = stateRef.current;
			if (!target || target.index !== idx || target.hlOnly !== hlOnly) return;
			setIsSeeking(true);
			// 0 reads as "no seek" in the player.
			setSkipToTime(target.ms || 0.0001);
		};
		playerInstance.addEventListener("can-play", onCanPlay);
		return () => playerInstance.removeEventListener("can-play", onCanPlay);
	}, [playerInstance, setIsSeeking, setSkipToTime]);

	const startNextSession = useCallback(() => {
		const { currentPlaylist: pl, isHighlightsOnlyMode: hlOnly, isPortal: portal } = stateRef.current;
		const len = pl.length;
		if (len === 0) return;
		setIsPaused(false);
		if (!portal) setSegmentsWatched([]);
		setCurrentSessionIndex((prev) => {
			let next = prev < len - 1 ? prev + 1 : 0;
			let attempts = 0;
			while (
				(!pl[next]?.poster || (hlOnly && !pl[next]?.highlightsOnlyStream)) &&
				next !== prev &&
				attempts < len
			) {
				next = next < len - 1 ? next + 1 : 0;
				attempts++;
			}
			if (hlOnly && !pl[next]?.highlightsOnlyStream) return 0;
			return pl[next]?.poster ? next : prev;
		});
	}, []);

	const startPreviousSession = useCallback(() => {
		const { currentPlaylist: pl, isPortal: portal } = stateRef.current;
		setIsPaused(false);
		if (!portal) setSegmentsWatched([]);
		setCurrentSessionIndex((prev) => {
			if (prev <= 0) return prev;
			let next = prev - 1;
			let attempts = 0;
			while (!pl[next]?.poster && next > 0 && attempts < prev) {
				next--;
				attempts++;
			}
			return pl[next]?.poster ? next : prev;
		});
	}, []);

	// ─── effects ──────────────────────────────────────────────────────────────

	// Reset highlight index when session changes
	useEffect(() => {
		const t = setTimeout(() => setCurrentHighlightIndex(null), 0);
		return () => clearTimeout(t);
	}, [currentSessionIndex]);

	// Advance to next session when the player reaches the end
	useEffect(() => {
		if (!playerInstance) return;
		const onEnd = () => {
			if (playerInstance.duration !== 0) {
				const { currentPlaylist: pl, isHighlightsOnlyMode: hlOnly, isPortal: portal } = stateRef.current;
				if (!portal) setSegmentsWatched([]);
				setCurrentSessionIndex((prev) => {
					const next = prev < pl.length - 1 ? prev + 1 : 0;
					if (hlOnly) {
						const resolved = resolveHlOnlyIndex(next, pl);
						return resolved === -1 ? 0 : resolved;
					}
					return next;
				});
			}
		};
		playerInstance.addEventListener("ended", onEnd);
		return () => playerInstance.removeEventListener("ended", onEnd);
	}, [playerInstance]);

	// ─── tracking helpers ─────────────────────────────────────────────────────

	function getSegmentAt(
		time: number,
		playlist: PlaylistItem,
		hlOnly: boolean,
	): [segment | undefined, number] {
		let playingIndex = -1;
		const next = playlist?.segments?.find((s: segment, i: number) => {
			const start = hlOnly ? s.highlightsOnlyStart : s.start;
			const end = hlOnly ? s.highlightsOnlyEnd : s.end;
			if (start <= time && end >= time) {
				playingIndex = i;
				return true;
			}
			return false;
		});
		return [next, playingIndex];
	}

	function onSegmentChanged(newSegment: segment) {
		if (!newSegment) return;
		const { currentPlaylist: pl, currentSessionIndex: idx, segmentsWatched: watched, isHighlightsOnlyMode: hlOnly } =
			stateRef.current;

		if (hlOnly && !watched.includes(newSegment.id)) {
			const src = pl[idx]?.highlightsOnlyStream;
			if (src) {
				setSegmentsWatched((prev) => [...prev, newSegment.id]);
				TrackEvent(
					"Highlight View",
					new Map([
						["name", newSegment.title],
						["id", newSegment.id],
						["streamUrl", src.src],
					]),
				);
			}
		} else if (!watched.includes(newSegment.id)) {
			setSegmentsWatched((prev) => [...prev, newSegment.id]);
			TrackEvent(
				newSegment.isHighlight ? "Highlight View" : "Segment View",
				new Map([
					["name", newSegment.title],
					["id", newSegment.id],
					["streamUrl", pl[idx]?.fullStream],
				]),
			);
		}
	}

	function trackWatchedTime(trackedSegment: segment) {
		if (!trackedSegment || !lastReportedTimestampRef.current) return;
		const { isPaused: paused } = stateRef.current;
		const elapsed = Date.now() - lastReportedTimestampRef.current.getTime();
		if (elapsed >= 500) {
			TrackEvent(
				"TimeWatched",
				new Map([
					["id", trackedSegment.id ?? "-"],
					["name", trackedSegment.title ?? "-"],
					["Milliseconds", elapsed.toString()],
				]),
			);
			if (!paused) lastReportedTimestampRef.current = new Date();
		}
		if (paused) lastReportedTimestampRef.current = null;
	}

	function trackSegmentChanged() {
		const {
			currentSegment: seg,
			currentPlaylist: pl,
			currentSessionIndex: idx,
			isHighlightsOnlyMode: hlOnly,
			isSeeking: seeking,
			currentPlaybackTime: time,
		} = stateRef.current;

		// Don't overwrite a manually-set segment while the player is seeking
		if (seeking) return;

		const start = hlOnly ? seg?.highlightsOnlyStart : seg?.start;
		const end = hlOnly ? seg?.highlightsOnlyEnd : seg?.end;

		if (seg && time * 1000 >= (start ?? 0) && time * 1000 <= (end ?? Infinity)) {
			return;
		}

		const currentItem = pl[idx] as PlaylistItem;
		const [nextSegment, playingIndex] = getSegmentAt(time * 1000, currentItem, hlOnly);

		if (nextSegment && seg !== nextSegment) {
			onSegmentChanged(nextSegment);
			if (seg) trackWatchedTime(seg);
			setCurrentSegment(nextSegment);
			setCurrentSegmentIndex(playingIndex);
			const highlightIdx = nextSegment.isHighlight
				? (pl[idx]?.highlights?.findIndex((s) => s.id === nextSegment.id) ?? -1)
				: -1;
			setCurrentHighlightIndex(highlightIdx >= 0 ? highlightIdx : null);
		}
	}

	// Segment tracking
	useEffect(() => {
		trackSegmentChanged();
	}, [currentPlaybackTime]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!isPaused && lastReportedTimestampRef.current == null) {
			lastReportedTimestampRef.current = new Date();
			trackSegmentChanged();
		}
	}, [isPaused]); // eslint-disable-line react-hooks/exhaustive-deps

	return {
		currentPlaylist,
		setCurrentPlaylist,
		currentSessionIndex,
		setCurrentSessionIndex: goToSession,
		currentSegmentIndex,
		setCurrentSegmentIndex,
		currentSegment,
		setCurrentSegment,
		currentHighlightIndex,
		setCurrentHighlightIndex,
		isHighlightsOnlyMode,
		setIsHighlightsOnlyMode: changeHighlightsOnlyMode,
		isPortal,
		setIsPortal,
		segmentsWatched,
		setSegmentsWatched,
		session,
		firstIndexWithHighlights,
		startNextSession,
		startPreviousSession,
	};
}
